import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock, Users, Award, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { erpPrograms, trainingTracks } from '@/lib/api';

export default function ERPDetailPage() {
  const { erp } = useParams();
  const program = erpPrograms[erp?.toLowerCase()];

  if (!program) {
    return <Navigate to="/programs" replace />;
  }

  return (
    <div className="min-h-screen pt-24" data-testid={`erp-detail-${erp}`}>
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              
              
            >
              <Link to="/programs" className="inline-flex items-center text-sm text-brand-600 hover:text-brand-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${program.color} flex items-center justify-center text-3xl mb-6`}>
                {program.icon}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-4">
                {program.fullName} Training
              </h1>
              <p className="text-xl text-brand-600 font-medium mb-4">{program.tagline}</p>
              <p className="text-lg text-slate-600 mb-8">{program.description}</p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/pricing">
                  <Button className="btn-primary" data-testid="enroll-now-btn">
                    Enroll Now
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="btn-secondary">
                    Free Demo Session
                  </Button>
                </Link>
              </div>
            </div>
            
            <div
              
              
              
            >
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-brand-900">Program Highlights</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-brand-700" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-900">Duration</p>
                      <p className="text-sm text-slate-600">6-12 weeks (track dependent)</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-brand-700" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-900">Class Size</p>
                      <p className="text-sm text-slate-600">Max 20 participants per batch</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-brand-700" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-900">Certification</p>
                      <p className="text-sm text-slate-600">Exam preparation included</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-brand-700" />
                    </div>
                    <div>
                      <p className="font-medium text-brand-900">Hands-On Labs</p>
                      <p className="text-sm text-slate-600">Real ERP system access</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="container-main">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using {program.name}</p>
          <div className="flex flex-wrap justify-center gap-4">
            {program.industries.map((industry, i) => (
              <span key={i} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm">
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Training Tracks */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Choose Your Training Track</h2>
            <p className="section-subheading mx-auto">
              Select the learning path that best matches your experience level and career goals
            </p>
          </div>
          
          <Tabs defaultValue="career_starter" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 mb-12">
              {Object.entries(trainingTracks).map(([key, track]) => (
                <TabsTrigger key={key} value={key} className="data-[state=active]:bg-brand-700 data-[state=active]:text-white">
                  {track.name}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {Object.entries(trainingTracks).map(([key, track]) => (
              <TabsContent key={key} value={key}>
                <div
                  
                  
                >
                  <Card className="max-w-4xl mx-auto">
                    <CardContent className="p-8 lg:p-10">
                      <div className="flex items-start space-x-4 mb-6">
                        <span className="text-4xl">{track.icon}</span>
                        <div>
                          <h3 className="text-2xl font-bold text-brand-900">{track.name}</h3>
                          <p className="text-brand-600 font-medium">{track.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-slate-600 mb-8">{track.description}</p>
                      
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="font-semibold text-brand-900 mb-4">What You'll Learn</h4>
                          <ul className="space-y-3">
                            {track.features.map((feature, i) => (
                              <li key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-5 h-5 text-brand-500 mt-0.5 flex-shrink-0" />
                                <span className="text-slate-600">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-brand-900 mb-4">Program Details</h4>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-slate-500">Duration</p>
                              <p className="font-medium text-brand-900">{track.duration}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Format</p>
                              <p className="font-medium text-brand-900">Live Online (Zoom/Teams)</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">Schedule</p>
                              <p className="font-medium text-brand-900">Weekday & Weekend batches available</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-8 pt-8 border-t border-slate-100">
                        <Link to="/pricing">
                          <Button className="btn-primary">
                            View Pricing for {track.name}
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Modules */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">{program.name} Modules Covered</h2>
            <p className="section-subheading mx-auto">
              Comprehensive curriculum covering all essential {program.name} functionalities
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {program.modules.map((module, index) => (
              <div
                key={module}
                
                
                
                
              >
                <Card className="program-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                      <span className="text-brand-700 font-mono font-bold">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <p className="font-medium text-brand-900">{module}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Career */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-brand-900 mb-6">Certification Alignment</h2>
              <p className="text-slate-600 mb-6">
                Our curriculum is aligned with official {program.name} certification exams, 
                preparing you for industry-recognized credentials.
              </p>
              <ul className="space-y-3">
                {program.certifications.map((cert, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className="w-5 h-5 text-brand-500 mt-0.5" />
                    <span className="text-slate-700">{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-brand-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                {program.name} skills are in high demand. Here are some roles you can pursue 
                after completing our training.
              </p>
              <ul className="space-y-3">
                {program.jobRoles.map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-brand-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-brand-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master {program.fullName}?
            </h2>
            <p className="text-lg text-brand-100 mb-8">
              Join our next batch and start your journey to becoming a certified {program.name} professional.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request More Info
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
